import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { orgDashboardApiIsomorphicFetchListingApplicationsWithPagination } from '#/modules/orgDashboard/api/listingApplication/orgDashboardApiFetchListingApplicationsWithPagination';
import { orgDashboardApiIsomorphicFetchOrg } from '#/modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardViewAllVolunteersRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { OrgDashboardOrgSubLayoutHandleProps } from '#/modules/orgDashboard/sub-layouts/Org/OrgDashboardOrgSubLayoutHandleProps';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';

import { OrgDashboardViewAllVolunteersPageLoadable } from './OrgDashboardViewAllVolunteersPageLoadable';

export const handle = {
  fallbackUrlByLocaleToCurrent: true,
  darkBackground: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps & OrgDashboardOrgSubLayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardViewAllVolunteersRoute>) {
  const searchParams = getAppRouteSearchParams({
    url: request.url,
    route: orgDashboardViewAllVolunteersRoute,
  });

  const pageIndex = parseInt(searchParams.page ?? '1', 10);

  const loadPromise = OrgDashboardViewAllVolunteersPageLoadable.load();
  const orgPromise = orgDashboardApiIsomorphicFetchOrg({
    request,
    context,
  })(orgId);

  const listingApplicationsResponse =
    await orgDashboardApiIsomorphicFetchListingApplicationsWithPagination({
      request,
      context,
    })(orgId, {
      withPagination: true,
      listingType: 'VOLOP',
      page: pageIndex - 1,
    });

  await loadPromise;
  const { org } = await orgPromise;

  const { listingApplications } = listingApplicationsResponse;
  const listingApplicationsMeta = listingApplicationsResponse;

  if (!listingApplications || !org?.membership.isActive) {
    await NotFoundPageLoadable.load();
  }

  return { org, listingApplications, listingApplicationsMeta };
}

export default function OrgDashboardViewAllVolunteersHorizonRoute({
  loaderData: { org, listingApplications, listingApplicationsMeta },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!listingApplications || !org?.membership.isActive) {
    return <NotFoundPageLoadable />;
  }

  return (
    <RouteModule title={i18n('View All Volunteers')}>
      <OrgDashboardViewAllVolunteersPageLoadable
        key="allvolunteerspage"
        listingApplications={listingApplications}
        listingApplicationsMeta={listingApplicationsMeta}
        org={org}
      />
    </RouteModule>
  );
}
